import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Table, Card, Typography, Grid, Select, Row, Col, Empty, Tag, Input, Divider, Button, Tooltip } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { Translate } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { EvaluationNotesStatus, getAllEvaluations, getEvaluationNotesStatuses } from './evaluation.reducer';
import { getEcole } from '../ecole/ecole.reducer';
import { getClasse, getClasses } from '../classe/classe.reducer';
import { getCycles } from '../cycle/cycle.reducer';
import { getEntities as getMatieres } from '../matiere-ecole/matiere-ecole.reducer';
import { getPeriodes } from 'app/entities/periode-evaluation/periode-evaluation.reducer';
import { getAnneeScolaire } from 'app/entities/annee-scolaire/annee-scolaire.reducer';
import { EvaluationType } from 'app/shared/model/evaluation.model';
import { StatutPeriodeEvaluation } from 'app/shared/model/periode-evaluation.model';

const { Title } = Typography;
const { useBreakpoint } = Grid;

const getEvaluationDateSortValue = (date?: string | null) => {
  if (!date) return Number.NEGATIVE_INFINITY;
  const timestamp = Date.parse(`${date}T00:00:00`);
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

const getPeriodeStatusTag = (statut?: StatutPeriodeEvaluation) => {
  const isActive = statut === StatutPeriodeEvaluation.OUVERTE;
  return <Tag color={isActive ? 'green' : 'default'}>{isActive ? 'Actif' : 'Inactif'}</Tag>;
};

export const Evaluation = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string }>) => {
  const dispatch = useAppDispatch();
  const evaluations = useAppSelector(state => state.evaluation.entities);
  const loading = useAppSelector(state => state.evaluation.loading);
  const cycles = useAppSelector(state => state.cycle.entities);
  const classes = useAppSelector(state => state.classe.entities);
  const classeEntity = useAppSelector(state => state.classe.entity);
  const matieres = useAppSelector(state => state.matiereEcole.entities);
  const periodes = useAppSelector(state => state.periodeEvaluation.entities);
  const { anneeScolaireId } = props.match.params;
  const noteStatusMap = useAppSelector(state => state.evaluation.noteStatusMap);
  const screens = useBreakpoint();
  const { ecoleid } = props.match.params;

  const [cycleId, setCycleId] = useState<string | null>(null);
  const [classeId, setClasseId] = useState<string | null>(null);
  const [periodeId, setPeriodeId] = useState<string | null>(null);
  const [matiereId, setMatiereId] = useState<string | null>(null);
  const [type, setType] = useState<EvaluationType | null>(null);
  const [notesStatus, setNotesStatus] = useState<EvaluationNotesStatus | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(getEcole(props.match.params.ecoleid));
    dispatch(getCycles(props.match.params.ecoleid));
    dispatch(getClasses(props.match.params.ecoleid));
    dispatch(getMatieres({ ecoleId: props.match.params.ecoleid }));
    dispatch(getAnneeScolaire(anneeScolaireId));
  }, []);

  useEffect(() => {
    if (classeId) {
      dispatch(getClasse(classeId));
    } else {
      setPeriodeId(null);
    }
  }, [classeId]);

  useEffect(() => {
    if (classeEntity?.id === classeId && classeEntity?.cycleId && anneeScolaireId) {
      dispatch(getPeriodes({ ecoleId: props.match.params.ecoleid, anneeScolaireId, cycleId: classeEntity.cycleId }));
    }
  }, [classeEntity?.id, classeEntity?.cycleId, classeId, anneeScolaireId]);

  useEffect(() => {
    if (cycleId && anneeScolaireId) {
      dispatch(getPeriodes({ ecoleId: props.match.params.ecoleid, anneeScolaireId, cycleId }));
    }
  }, [cycleId, anneeScolaireId]);

  useEffect(() => {
    if (!cycleId) return;

    const openedPeriode = periodes.find(periode => periode.cycleId === cycleId && periode.statut === StatutPeriodeEvaluation.OUVERTE);
    setPeriodeId(openedPeriode?.id || null);
  }, [cycleId, periodes]);

  useEffect(() => {
    if (anneeScolaireId) {
      dispatch(getAllEvaluations({ ecoleId: props.match.params.ecoleid, anneeScolaireId }));
    }
  }, [anneeScolaireId]);

  const classesById = useMemo(() => new Map(classes.map(classe => [classe.id, classe])), [classes]);
  const cyclesById = useMemo(() => new Map(cycles.map(cycle => [cycle.id, cycle])), [cycles]);

  const filteredClasses = useMemo(() => {
    return cycleId ? classes.filter(classe => classe.cycleId === cycleId) : classes;
  }, [classes, cycleId]);

  const baseFilteredEvaluations = useMemo(() => {
    return evaluations
      .filter(e => {
        if (cycleId && classesById.get(e.classeId)?.cycleId !== cycleId) return false;
        if (classeId && e.classeId !== classeId) return false;
        if (periodeId && e.periodeId !== periodeId) return false;
        if (matiereId && e.matiereId !== matiereId) return false;
        if (type && e.type !== type) return false;
        if (search && !`${e.titre || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => getEvaluationDateSortValue(b.date) - getEvaluationDateSortValue(a.date));
  }, [evaluations, classesById, cycleId, classeId, periodeId, matiereId, type, search]);

  const filteredEvaluations = useMemo(() => {
    return baseFilteredEvaluations.filter(e => {
      if (notesStatus && (noteStatusMap[e.id] || 'Non saisie') !== notesStatus) return false;
      return true;
    });
  }, [baseFilteredEvaluations, noteStatusMap, notesStatus]);

  useEffect(() => {
    const ids = baseFilteredEvaluations.map(e => e.id).filter((id): id is string => Boolean(id));
    const missingIds = ids.filter(id => !noteStatusMap[id]);
    if (missingIds.length) {
      dispatch(getEvaluationNotesStatuses(missingIds));
    }
  }, [baseFilteredEvaluations, dispatch, noteStatusMap]);

  const formatTableDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date).replace(/\//g, '-');
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render(value) {
        return formatTableDate(value);
      },
    },
    {
      title: 'Cycle',
      key: 'cycle',
      render(_value, record) {
        const classe = classesById.get(record.classeId);
        return classe?.cycleNom || cyclesById.get(classe?.cycleId)?.nom || '-';
      },
    },
    {
      title: 'Classe',
      dataIndex: 'classeNom',
      key: 'classeNom',
      render(value) {
        return value || '-';
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render(t) {
        return t ? (
          <Tag color={t === EvaluationType.COMPOSITION ? 'blue' : t === EvaluationType.CONTROLE ? 'green' : 'gold'}>{t}</Tag>
        ) : (
          '-'
        );
      },
    },
    { title: 'Titre', dataIndex: 'titre', key: 'titre' },
    {
      title: 'Matière',
      dataIndex: 'matiereNom',
      key: 'matiereNom',
      render(value, record) {
        return record.sousMatiereNom ? `${value || '-'} / ${record.sousMatiereNom}` : value || '-';
      },
    },
    {
      title: 'Statut notes',
      dataIndex: 'id',
      key: 'notesStatus',
      render(id) {
        const statut = noteStatusMap[id] || 'Non saisie';
        const color = statut === 'Terminée' ? 'green' : statut === 'En cours' ? 'gold' : 'red';
        return <Tag color={color}>{statut}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: screens.xs ? 90 : 120,
      render(_value, record) {
        return (
          <Tooltip title="Saisir les notes">
            <Button
              type="text"
              icon={<FileTextOutlined />}
              disabled={!record.id || !record.classeId}
              onClick={() =>
                props.history.push(
                  `/notes/${ecoleid}/${anneeScolaireId}?evaluationId=${record.id}&classeId=${record.classeId}&returnTo=evaluation`
                )
              }
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Card style={{ margin: screens.xs ? '16px 8px' : '24px 24px' }} bodyStyle={{ padding: screens.xs ? '0 8px 16px' : '0 24px 24px' }}>
      <div
        style={{
          padding: screens.xs ? '16px 0' : '24px 0',
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'flex-start' : 'center',
          gap: screens.xs ? '16px' : '0',
        }}
      >
        <Title level={2} style={{ margin: 0, fontSize: screens.xs ? '1.5rem' : '2rem' }}>
          <Translate contentKey="korischoolApp.evaluation.home.title">Évaluations</Translate>
        </Title>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Select
              placeholder="Cycle"
              style={{ width: '100%' }}
              value={cycleId || undefined}
              onChange={v => {
                setCycleId(v || null);
                setClasseId(null);
                setPeriodeId(null);
              }}
              allowClear
            >
              {cycles.map(cycle => (
                <Select.Option key={cycle.id} value={cycle.id}>
                  {cycle.nom}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Période"
              style={{ width: '100%' }}
              value={periodeId || undefined}
              onChange={v => setPeriodeId(v || null)}
              allowClear
              disabled={!cycleId && !classeId}
            >
              {periodes.map(periode => (
                <Select.Option key={periode.id} value={periode.id}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span>{periode.nom}</span>
                    {getPeriodeStatusTag(periode.statut)}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Classe"
              style={{ width: '100%' }}
              value={classeId || undefined}
              onChange={v => setClasseId(v || null)}
              allowClear
            >
              {filteredClasses.map(classe => (
                <Select.Option key={classe.id} value={classe.id}>
                  {classe.nom}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select placeholder="Type" style={{ width: '100%' }} value={type || undefined} onChange={v => setType(v || null)} allowClear>
              <Select.Option value={EvaluationType.DEVOIR}>Devoir</Select.Option>
              <Select.Option value={EvaluationType.COMPOSITION}>Composition</Select.Option>
              <Select.Option value={EvaluationType.CONTROLE}>Contrôle</Select.Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Matière"
              style={{ width: '100%' }}
              value={matiereId || undefined}
              onChange={v => setMatiereId(v || null)}
              allowClear
            >
              {matieres.map(matiere => (
                <Select.Option key={matiere.id} value={matiere.id}>
                  {matiere.nom}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Statut"
              style={{ width: '100%' }}
              value={notesStatus || undefined}
              onChange={v => setNotesStatus(v || null)}
              allowClear
            >
              <Select.Option value="Non saisie">Non saisie</Select.Option>
              <Select.Option value="En cours">En cours</Select.Option>
              <Select.Option value="Terminée">Terminée</Select.Option>
            </Select>
          </Col>
          <Col xs={24}>
            <Input.Search placeholder="Rechercher par titre..." allowClear value={search} onChange={e => setSearch(e.target.value)} />
          </Col>
        </Row>
      </Card>

      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Typography.Text type="secondary">{filteredEvaluations.length} évaluation(s)</Typography.Text>
        </Col>
      </Row>

      <Divider style={{ margin: '8px 0' }} />

      {filteredEvaluations.length === 0 && !loading ? (
        <Empty description="Aucune évaluation trouvée" />
      ) : (
        <Table
          dataSource={filteredEvaluations}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          loading={loading}
          locale={{
            emptyText: <Translate contentKey="korischoolApp.evaluation.home.notFound">No evaluations found</Translate>,
          }}
          bordered
          size="middle"
          style={{ backgroundColor: 'white' }}
          scroll={{ x: screens.xs ? 'true' : 'false' }}
        />
      )}
    </Card>
  );
};

export default Evaluation;
